import { useLoggedInMember } from "@/modules/auth/states";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import { ZTable } from "@/shared/components/table";
import { whitelistDomainQueryKeys } from "@/shared/constants/QueryKeys";
import { useIsAdmin } from "@/shared/hooks/use-is-admin";
import { ROUTES } from "@/shared/constants/routes";
import { addNotification } from "@/shared/states/notification";
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from "@nextui-org/react";
import { ArrowLeft, Copy, Globe, Lock, MoreVertical, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getWhitelistedDomainsList, revokeWhitelistedDomain, type WhitelistDomain } from "../../requests/whitelist";
import AddDomainModal from "./AddDomainModal";
import RemoveDomainModal from "./RemoveDomainModal";

const WhitelistedDomainsList = () => {
  const member = useLoggedInMember();
  const queryClient = useQueryClient();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [domainToRemove, setDomainToRemove] = useState<WhitelistDomain | null>(null);
  const [copiedDomainId, setCopiedDomainId] = useState<string | null>(null);
  const copiedStateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const userDomain = useMemo(() => {
    const emailDomain = member?.email_address?.split("@")[1] || "";
    return emailDomain.toLowerCase();
  }, [member?.email_address]);

  const {
    data: domains = [],
    isLoading,
    isFetching,
    error: fetchError,
    isError,
  } = useQuery({
    queryKey: whitelistDomainQueryKeys.all,
    queryFn: getWhitelistedDomainsList,
    staleTime: 60 * 1000,
  });

  const removeDomainMutation = useMutation({
    mutationFn: revokeWhitelistedDomain,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: whitelistDomainQueryKeys.all });
    },
  });

  useEffect(() => {
    if (!isError) return;
    const errorMessage =
      fetchError instanceof Error ? fetchError.message : "Something went wrong. Please try again later!";

    addNotification({
      type: "error",
      title: "Failed to fetch whitelisted domains",
      message: errorMessage,
    });
  }, [isError, fetchError]);

  useEffect(() => {
    const handleOpenAddModal = () => {
      setIsAddModalOpen(true);
    };

    window.addEventListener("openAddWhitelistDomainModal", handleOpenAddModal);

    return () => {
      window.removeEventListener("openAddWhitelistDomainModal", handleOpenAddModal);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (copiedStateTimerRef.current) {
        clearTimeout(copiedStateTimerRef.current);
        copiedStateTimerRef.current = null;
      }
    };
  }, []);

  const handleCopyLink = useCallback(async (domainId: string, link: string) => {
    if (!link) return;

    try {
      await navigator.clipboard.writeText(link);
      setCopiedDomainId(domainId);

      if (copiedStateTimerRef.current) {
        clearTimeout(copiedStateTimerRef.current);
      }

      copiedStateTimerRef.current = setTimeout(() => {
        setCopiedDomainId((currentValue) => (currentValue === domainId ? null : currentValue));
      }, 1500);
    } catch {
      addNotification({
        type: "error",
        title: "Failed to copy access link",
        message: "Please try again.",
      });
    }
  }, []);

  const isOrganizationDomain = useCallback(
    (domain: string) => {
      const normalizedDomain = domain.trim().toLowerCase().replace(/^@/, "");
      return normalizedDomain === userDomain;
    },
    [userDomain],
  );

  const handleRemoveClick = useCallback(
    (domain: WhitelistDomain) => {
      if (!isAdmin) return;
      setDomainToRemove(domain);
      setIsRemoveModalOpen(true);
    },
    [isAdmin],
  );

  const handleRemoveModalClose = useCallback(() => {
    if (removeDomainMutation.isPending) return;
    setIsRemoveModalOpen(false);
    setDomainToRemove(null);
  }, [removeDomainMutation.isPending]);

  const handleRemoveConfirm = useCallback(async () => {
    if (!isAdmin || removeDomainMutation.isPending || !domainToRemove) return;

    try {
      const selectedDomain = domainToRemove;
      await removeDomainMutation.mutateAsync(selectedDomain.id);
      addNotification({
        type: "success",
        message: `Removed ${selectedDomain.email_domain} successfully.`,
      });
      setIsRemoveModalOpen(false);
      setDomainToRemove(null);
    } catch (error) {
      addNotification({
        type: "error",
        title: "Failed to remove domain",
        message: error instanceof Error ? error.message : "Something went wrong. Please try again later!",
      });
    }
  }, [isAdmin, removeDomainMutation, domainToRemove]);

  const renderAccessLink = useCallback(
    (row: WhitelistDomain) => {
      // full_url is the preferred access URL; falls back to custom_access_link
      const link = row.full_url || row.custom_access_link || "";

      return (
        <div className="flex items-center gap-2 min-w-0 group">
          <Globe size={14} className="text-[#717179] shrink-0" />
          <span className="text-[#333] text-sm truncate">{link}</span>
          <Button
            isIconOnly
            variant="light"
            aria-label={`Copy access link for ${row.email_domain}`}
            className="min-w-[24px] w-6 h-6 p-0 bg-transparent shrink-0"
            onPress={() => {
              void handleCopyLink(row.id, link);
            }}
            isDisabled={!link}
          >
            <Copy size={14} className={copiedDomainId === row.id ? "text-green-500" : "text-[#717179]"} />
          </Button>
        </div>
      );
    },
    [handleCopyLink, copiedDomainId],
  );

  const renderActions = useCallback(
    (row: WhitelistDomain) => {
      if (!isAdmin || isOrganizationDomain(row.email_domain)) {
        return (
          <div className="flex justify-end items-center h-10 px-2">
            <Lock size={16} className="text-[#717179]" />
          </div>
        );
      }

      return (
        <div className="flex justify-end items-center h-10">
          <Dropdown placement="bottom-end">
            <DropdownTrigger>
              <Button
                isIconOnly
                variant="light"
                aria-label={`Actions for ${row.email_domain}`}
                className="bg-transparent"
                isDisabled={isLoading || isFetching}
              >
                <MoreVertical size={20} className="text-[#717179]" />
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Whitelisted Domain Actions"
              variant="flat"
              onAction={(key) => {
                if (key === "remove") {
                  handleRemoveClick(row);
                }
              }}
            >
              <DropdownItem key="remove" color="danger" startContent={<Trash2 size={16} />} className="text-danger">
                Remove
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      );
    },
    [isAdmin, isOrganizationDomain, isLoading, isFetching, handleRemoveClick],
  );

  if (!isAdmin) {
    return <Navigate to={`/${ROUTES.SETTINGS}`} state={{ tab: "general" }} replace />;
  }

  const isTableLoading = isLoading || isFetching;

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-center px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`/${ROUTES.SETTINGS}`, { state: { tab: "general" }, replace: true })}
            className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <h1 className="text-sm font-medium text-gray-900">Whitelisted Domains</h1>
        </div>
      </div>
      <div className="grow w-full overflow-hidden flex flex-col">
        <div className="grow overflow-hidden flex">
          <div className="flex-1 flex flex-col bg-white overflow-auto p-6">
            <ZTable<WhitelistDomain>
              className="border-1 border-[#E4E4E7] rounded-lg overflow-auto scroll-auto"
              rows={domains}
              columns={[
                {
                  type: "string",
                  fieldName: "email_domain",
                  headerName: "Domain",
                  sortable: true,
                  renderCell: (row) => {
                    return <span className="text-sm text-[#006FEE]">{row.email_domain}</span>;
                  },
                },
                {
                  type: "string",
                  fieldName: "full_url",
                  headerName: "Custom Access Link",
                  sortable: false,
                  renderCell: renderAccessLink,
                },
                {
                  type: "custom",
                  fieldName: "actions",
                  headerName: "",
                  renderCell: renderActions,
                  className: "w-[60px]",
                },
              ]}
              isHeaderSticky={true}
              isCompact={true}
              removeWrapper={true}
              getRowId={(row) => row.id}
              tableHeaderProps={{
                className: "shadow-none rounded-none",
              }}
              tableBodyProps={{
                emptyContent: isTableLoading ? (
                  <div className="flex items-center justify-center">
                    <LoadingSpinner containerClassName="bg-transparent relative" className="mx-auto" />
                  </div>
                ) : (
                  "No whitelisted domains found"
                ),
              }}
              tableRowProps={(_, rowIndex) => ({
                className: `${rowIndex === 0 ? "border-none" : "border-y"} hover:bg-[#F2F2F2]`,
              })}
              classNames={{
                wrapper: "shadow-none rounded-none",
              }}
              aria-label="Whitelisted domains table"
            />
          </div>
        </div>
      </div>

      <AddDomainModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
        }}
        onSuccess={async () => {
          await queryClient.invalidateQueries({ queryKey: whitelistDomainQueryKeys.all });
        }}
      />

      <RemoveDomainModal
        isOpen={isRemoveModalOpen}
        onClose={handleRemoveModalClose}
        onConfirm={handleRemoveConfirm}
        domain={domainToRemove}
        isLoading={removeDomainMutation.isPending}
      />
    </div>
  );
};

export default WhitelistedDomainsList;
